<template>
  <component :is="rootTag" v-if="hasBenefit || loading" :class="rootClass">
    <!-- Title and subtitle, dropped in compact mode -->
    <div v-if="!compact">
      <div class="flex items-center gap-1.5">
        <component :is="embedded ? 'p' : TextTitle" class="font-medium">
          Optima Voordeel
        </component>
        <WikiTooltipComponent path="/nl/platform/results/optima-benefit" />
      </div>
      <TextDescription>
        Voordeel per maand dat is behaald door Optima-aansturing.
      </TextDescription>
    </div>
    <LoadingWrapper :loading :error="loadingFailed">
      <div :class="compact ? '' : 'flex flex-wrap items-end gap-6'">
        <KpiComponent
          label="Optima Voordeel"
          :value="optimaBenefit ?? 0"
          :precision="2"
          format="€ _"
          icon="network"
          icon-color="primary"
          :show-warning="hasMissingMeasurementData"
        >
          <template #description="{ label }">
            <span class="inline-flex flex-row items-center gap-1">
              {{ label }}
              <WikiTooltipComponent
                v-if="compact"
                path="/nl/platform/results/optima-benefit"
              />
              <BaseTooltip
                v-if="hasMissingMeasurementData"
                color="danger"
                description="Sommige meetgegevens ontbreken. Het getoonde voordeel is mogelijk niet helemaal nauwkeurig."
              />
            </span>
          </template>
        </KpiComponent>
      </div>
    </LoadingWrapper>
  </component>
</template>

<script setup>
import useResultsData from "@/composables/useResultsData.js"
import {
  BaseCard,
  BaseTooltip,
  TextTitle,
  TextDescription,
  LoadingWrapper,
} from "@repowerednl/ui-component-library"
import KpiComponent from "@/components/information/KpiComponent.vue"
import WikiTooltipComponent from "@/components/information/WikiTooltipComponent.vue"
import { computed } from "vue"

const props = defineProps({
  meteringPoint: { type: Object, default: null },
  dateRange: { type: Array, required: true },
  /**
   * When embedded (e.g. inside the yields panel) the outer card and large title
   * are dropped so the block reads as a small sub-section.
   */
  embedded: { type: Boolean, default: false },
  /**
   * When compact (e.g. in a card header next to the export button) only the KPI
   * box is rendered: no title or subtitle, and the wiki tooltip moves into the
   * box itself.
   */
  compact: { type: Boolean, default: false },
})

const rootTag = computed(() =>
  props.embedded || props.compact ? "div" : BaseCard,
)

const rootClass = computed(() => {
  if (props.compact) {
    return ""
  }
  return props.embedded
    ? "flex flex-col gap-2"
    : "flex flex-col gap-2 p-4 md:gap-4 md:p-6"
})

const { optimaBenefit, hasMissingMeasurementData, loading, loadingFailed } =
  useResultsData(
    () => props.meteringPoint,
    () => props.dateRange,
    { loadOptimaBenefit: true },
  )

const hasBenefit = computed(
  () => optimaBenefit.value || optimaBenefit.value === 0,
)
</script>
